import React, { useEffect, useMemo, useState } from "react";
import API from "../../api/api";
import AdminLayout from "../../components/admin/AdminLayout";
import { useNavigate } from "react-router-dom";

const STEPS = [
  "Basic Information",
  "Age Divisions",
  "Review",
];

export default function TournamentCreate() {
  const navigate = useNavigate();

  const [step, setStep] = useState(0);

  const [form, setForm] = useState({
  name: "",
  startdate: "",
  enddate: "",
  location: "",
  age_divisions: [],
});

  const [ageDivisions, setAgeDivisions] = useState([]);
  const [loadingAges, setLoadingAges] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  // --------------------------------------------------
  // LOAD AVAILABLE AGE DIVISIONS
  // --------------------------------------------------

  useEffect(() => {
    async function loadAgeDivisions() {
      try {
        setLoadingAges(true);
        setError("");

        const res = await API.get(
          "/tournaments/available-age-divisions"
        );

        const data = res?.data ?? res;

        setAgeDivisions(
          Array.isArray(data) ? data : []
        );
      } catch (err) {
        console.error(
          "Age divisions load error:",
          err
        );

        setError(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Failed to load age divisions"
        );
      } finally {
        setLoadingAges(false);
      }
    }

    loadAgeDivisions();
  }, []);

  // --------------------------------------------------
  // SORT AGE DIVISIONS
  // --------------------------------------------------

  const sortedAgeDivisions = useMemo(() => {
    return [...ageDivisions].sort(
      (a, b) =>
        Number(a.min_age || 0) -
        Number(b.min_age || 0)
    );
  }, [ageDivisions]);

  // --------------------------------------------------
  // BASIC FORM CHANGE
  // --------------------------------------------------

  const updateForm = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // --------------------------------------------------
  // AGE DIVISION TOGGLE
  // --------------------------------------------------

  const toggleAgeDivision = (id) => {
    const numericId = Number(id);

    setForm((prev) => {
      const exists =
        prev.age_divisions.includes(numericId);

      return {
        ...prev,
        age_divisions: exists
          ? prev.age_divisions.filter(
              (x) => x !== numericId
            )
          : [
              ...prev.age_divisions,
              numericId,
            ],
      };
    });
  };

  const selectAllAges = () => {
    setForm((prev) => ({
      ...prev,
      age_divisions: sortedAgeDivisions.map(
        (d) => Number(d.agedivid)
      ),
    }));
  };

  const clearAllAges = () => {
    setForm((prev) => ({
      ...prev,
      age_divisions: [],
    }));
  };

  // --------------------------------------------------
  // VALIDATION
  // --------------------------------------------------

  const validateStep = () => {
    setError("");

    if (step === 0) {
      if (!form.name.trim()) {
        setError(
          "Tournament name is required."
        );
        return false;
      }

      if (!form.startdate) {
  setError("Start date is required.");
  return false;
}

if (!form.enddate) {
  setError("End date is required.");
  return false;
}

if (form.enddate < form.startdate) {
  setError("End date cannot be before start date.");
  return false;
}

return true;
    }

    if (step === 1) {
      if (!form.age_divisions.length) {
        setError(
          "Select at least one age division."
        );
        return false;
      }

      return true;
    }

    return true;
  };

  // --------------------------------------------------
  // NEXT
  // --------------------------------------------------

  const nextStep = () => {
    if (!validateStep()) return;

    setStep((prev) =>
      Math.min(prev + 1, STEPS.length - 1)
    );
  };

  // --------------------------------------------------
  // BACK
  // --------------------------------------------------

  const previousStep = () => {
    setError("");

    setStep((prev) =>
      Math.max(prev - 1, 0)
    );
  };

  // --------------------------------------------------
  // CREATE TOURNAMENT
  // --------------------------------------------------

  const handleSubmit = async () => {
    if (!validateStep()) return;

    try {
      setCreating(true);
      setError("");
      setResult(null);

      const payload = {
  name: form.name.trim(),
  startdate: form.startdate,
  enddate: form.enddate,
  location: form.location.trim(),
  age_divisions: form.age_divisions.map(Number),
};

      console.log(
        "Creating tournament:",
        payload
      );

      const res = await API.post(
        "/tournaments",
        payload
      );

      const data = res?.data ?? res;

      if (!data?.id) {
        throw new Error(
          "Tournament was created but no ID was returned."
        );
      }

      setResult({
        success: true,
        id: data.id,
      });

      setTimeout(() => {
        navigate(
          `/admin/tournament/${data.id}`
        );
      }, 1000);
    } catch (err) {
      console.error(
        "Create tournament error:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          "Failed to create tournament"
      );
    } finally {
      setCreating(false);
    }
  };

  // --------------------------------------------------
  // SELECTED AGE OBJECTS
  // --------------------------------------------------

  const selectedAgeObjects =
    sortedAgeDivisions.filter((division) =>
      form.age_divisions.includes(
        Number(division.agedivid)
      )
    );

  // --------------------------------------------------
  // RENDER
  // --------------------------------------------------

  return (
    <AdminLayout>
      <div className="min-h-screen bg-[#050505] text-white p-6 md:p-10">

        <div className="max-w-5xl mx-auto">

          {/* HEADER */}

          <div className="mb-10">
            <div className="text-yellow-500 text-xs font-black uppercase tracking-[0.25em] mb-2">
              WAR FC ADMIN
            </div>

            <h1 className="text-4xl md:text-5xl font-black uppercase italic tracking-tight">
              Create Tournament
            </h1>

            <p className="text-neutral-500 text-sm mt-2">
              Build the tournament structure before
              fighters begin registration.
            </p>
          </div>

          {/* STEP INDICATOR */}

          <div className="grid grid-cols-3 gap-2 mb-8">
            {STEPS.map((label, index) => {
              const active = index === step;
              const completed =
                index < step;

              return (
                <div
                  key={label}
                  className={`
                    rounded-xl border p-4
                    ${
                      active
                        ? "border-yellow-500 bg-yellow-500/10"
                        : completed
                        ? "border-green-500/40 bg-green-500/5"
                        : "border-white/10 bg-white/[0.02]"
                    }
                  `}
                >
                  <div
                    className={`
                      text-[10px] font-black uppercase tracking-widest
                      ${
                        active
                          ? "text-yellow-500"
                          : completed
                          ? "text-green-400"
                          : "text-neutral-600"
                      }
                    `}
                  >
                    Step {index + 1}
                  </div>

                  <div className="text-xs font-bold mt-1">
                    {label}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ERROR */}

          {error && (
            <div className="mb-6 rounded-xl border border-red-800/50 bg-red-950/30 p-4 text-red-400 text-sm font-bold">
              {error}
            </div>
          )}

          {/* SUCCESS */}

          {result?.success && (
            <div className="mb-6 rounded-xl border border-green-700/50 bg-green-950/30 p-4 text-green-400">
              <div className="font-black uppercase">
                Tournament created successfully!
              </div>

              <div className="text-xs mt-1">
                Tournament ID: #{result.id}
              </div>

              <div className="text-xs mt-2 text-green-500/70">
                Opening tournament management...
              </div>
            </div>
          )}

          {/* MAIN CARD */}

          <div className="bg-neutral-950 border border-white/10 rounded-3xl overflow-hidden">

            {/* -------------------------------------- */}
            {/* STEP 1 */}
            {/* -------------------------------------- */}

            {step === 0 && (
              <div className="p-6 md:p-10">

                <div className="mb-8">
                  <h2 className="text-xl font-black uppercase italic">
                    Basic Information
                  </h2>

                  <p className="text-neutral-500 text-xs mt-2">
                    Enter the basic information for the
                    tournament.
                  </p>
                </div>

                <div className="space-y-6 max-w-2xl">

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
                      Tournament Name
                    </label>

                    <input
                      value={form.name}
                      onChange={(e) =>
                        updateForm(
                          "name",
                          e.target.value
                        )
                      }
                      placeholder="WAR FC NATIONAL CHAMPIONSHIP"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl p-4 text-sm outline-none focus:border-yellow-500 transition"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">

  {/* START DATE */}
  <div>
    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
      Start Date
    </label>

    <input
      type="date"
      value={form.startdate}
      onChange={(e) =>
        updateForm("startdate", e.target.value)
      }
      className="w-full bg-white/[0.04] border border-white/10 rounded-xl p-4 text-sm outline-none focus:border-yellow-500 transition"
    />
  </div>

  {/* END DATE */}
  <div>
    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
      End Date
    </label>

    <input
      type="date"
      min={form.startdate || undefined}
      value={form.enddate}
      onChange={(e) =>
        updateForm("enddate", e.target.value)
      }
      className="w-full bg-white/[0.04] border border-white/10 rounded-xl p-4 text-sm outline-none focus:border-yellow-500 transition"
    />
  </div>

</div>

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-2">
                      Location
                    </label>

                    <input
                      value={form.location}
                      onChange={(e) =>
                        updateForm(
                          "location",
                          e.target.value
                        )
                      }
                      placeholder="Ulaanbaatar"
                      className="w-full bg-white/[0.04] border border-white/10 rounded-xl p-4 text-sm outline-none focus:border-yellow-500 transition"
                    />
                  </div>

                </div>
              </div>
            )}

            {/* -------------------------------------- */}
            {/* STEP 2 */}
            {/* -------------------------------------- */}

            {step === 1 && (
              <div className="p-6 md:p-10">

                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">

                  <div>
                    <h2 className="text-xl font-black uppercase italic">
                      Age Divisions
                    </h2>

                    <p className="text-neutral-500 text-xs mt-2">
                      Choose which age divisions are
                      available for this tournament.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={selectAllAges}
                      disabled={loadingAges}
                      className="px-4 py-2 rounded-lg bg-green-600/20 border border-green-600/30 text-green-400 text-[10px] font-black uppercase"
                    >
                      Select All
                    </button>

                    <button
                      type="button"
                      onClick={clearAllAges}
                      className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-neutral-400 text-[10px] font-black uppercase"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                {loadingAges ? (
                  <div className="py-16 text-center text-yellow-500 text-xs font-black uppercase tracking-widest animate-pulse">
                    Loading age divisions...
                  </div>
                ) : sortedAgeDivisions.length === 0 ? (
                  <div className="py-16 text-center border border-red-800/30 rounded-2xl bg-red-950/10">
                    <div className="text-red-400 text-sm font-black uppercase">
                      No age divisions available
                    </div>

                    <div className="text-neutral-500 text-xs mt-2">
                      Check the age_divisions table.
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">

                    {sortedAgeDivisions.map(
                      (division) => {
                        const id = Number(
                          division.agedivid
                        );

                        const selected =
                          form.age_divisions.includes(
                            id
                          );

                        return (
                          <button
                            type="button"
                            key={id}
                            onClick={() =>
                              toggleAgeDivision(id)
                            }
                            className={`
                              text-left rounded-2xl border p-5 transition-all
                              ${
                                selected
                                  ? "border-yellow-500 bg-yellow-500/10 shadow-[0_0_25px_rgba(234,179,8,0.08)]"
                                  : "border-white/10 bg-white/[0.02] hover:border-white/20"
                              }
                            `}
                          >

                            <div className="flex items-center justify-between">

                              <div>
                                <div
                                  className={`
                                    text-2xl font-black italic
                                    ${
                                      selected
                                        ? "text-yellow-400"
                                        : "text-white"
                                    }
                                  `}
                                >
                                  {division.name}
                                </div>

                                <div className="text-[10px] text-neutral-600 font-bold uppercase mt-1">
                                  Ages{" "}
                                  {division.min_age}
                                  –
                                  {division.max_age}
                                </div>
                              </div>

                              <div
                                className={`
                                  w-7 h-7 rounded-lg border flex items-center justify-center font-black
                                  ${
                                    selected
                                      ? "bg-yellow-500 border-yellow-500 text-black"
                                      : "border-white/20 text-transparent"
                                  }
                                `}
                              >
                                ✓
                              </div>

                            </div>
                          </button>
                        );
                      }
                    )}

                  </div>
                )}

                <div className="mt-8 p-5 rounded-2xl bg-white/[0.03] border border-white/10">
                  <div className="text-[10px] text-neutral-500 uppercase font-black tracking-widest">
                    Selected
                  </div>

                  <div className="text-2xl font-black text-yellow-400 mt-1">
                    {form.age_divisions.length}
                  </div>

                  <div className="text-xs text-neutral-500">
                    age divisions
                  </div>
                </div>

              </div>
            )}

            {/* -------------------------------------- */}
            {/* STEP 3 */}
            {/* -------------------------------------- */}

            {step === 2 && (
              <div className="p-6 md:p-10">

                <div className="mb-8">
                  <h2 className="text-xl font-black uppercase italic">
                    Review Tournament
                  </h2>

                  <p className="text-neutral-500 text-xs mt-2">
                    Check everything before creating
                    the tournament.
                  </p> 
                </div>

                <div className="space-y-6">

                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">

                    <ReviewBox
  label="End Date"
  value={
    form.enddate || "Not specified"
  }
/>

                    <ReviewBox
                      label="Start Date"
                      value={
                        form.startdate ||
                        "Not specified"
                      }
                    />

                    <ReviewBox
                      label="Location"
                      value={
                        form.location ||
                        "Not specified"
                      }
                    />

                  </div>

                  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">

                    <div className="text-[10px] font-black uppercase tracking-widest text-neutral-500 mb-4">
                      Age Divisions
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">

                      {selectedAgeObjects.map(
                        (division) => (
                          <div
                            key={
                              division.agedivid
                            }
                            className="rounded-xl bg-yellow-500/10 border border-yellow-500/20 p-4"
                          >
                            <div className="text-yellow-400 font-black text-lg italic">
                              {division.name}
                            </div>

                            <div className="text-neutral-600 text-[10px] uppercase font-bold mt-1">
                              {division.min_age}
                              –
                              {division.max_age}
                              years
                            </div>
                          </div>
                        )
                      )}

                    </div>

                  </div>

                  <div className="rounded-2xl border border-green-500/20 bg-green-500/5 p-5">

                    <div className="text-green-400 text-xs font-black uppercase">
                      Ready to create
                    </div>

                    <div className="text-neutral-500 text-xs mt-2">
                      The selected age divisions will
                      automatically be attached to this
                      tournament.
                    </div>

                  </div>

                </div>

              </div>
            )}

            {/* -------------------------------------- */}
            {/* FOOTER */}
            {/* -------------------------------------- */}

            <div className="border-t border-white/10 p-6 flex items-center justify-between gap-4">

              <button
                type="button"
                onClick={previousStep}
                disabled={step === 0 || creating}
                className="px-6 py-3 rounded-xl bg-white/5 border border-white/10 text-neutral-400 hover:text-white disabled:opacity-30 text-xs font-black uppercase"
              >
                ← Back
              </button>

              <div className="text-[10px] text-neutral-600 font-black uppercase tracking-widest hidden md:block">
                Step {step + 1} / {STEPS.length}
              </div>

              {step < STEPS.length - 1 ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-7 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-black uppercase"
                >
                  Continue →
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={creating}
                  className="px-8 py-3 rounded-xl bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black text-xs font-black uppercase"
                >
                  {creating
                    ? "Creating..."
                    : "Create Tournament"}
                </button>
              )}

            </div>

          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

// --------------------------------------------------
// REVIEW BOX
// --------------------------------------------------

function ReviewBox({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5">
      <div className="text-[9px] font-black uppercase tracking-widest text-neutral-600">
        {label}
      </div>

      <div className="text-sm font-black uppercase mt-2 break-words">
        {value}
      </div>
    </div>
  ); 
} 